import React from 'react';
import { Tooltip } from 'primereact/tooltip';
import { ShippingOrder } from '../../DeShippingTableWrapper';

// Types
interface PriorityStatusInfo {
  status: string;
  severity: string;
  icon: string;
  count: number;
  tooltipText: string;
}

interface OrderStatusTemplateProps {
  rowData: ShippingOrder;
  onClick?: (order: ShippingOrder, statusType: string) => void;
}

// Priority status calculation utility
const calculatePriorityStatus = (order: ShippingOrder): PriorityStatusInfo => {
  const { unassigned_items, pending_review_items, declined_items, assigned_items, total_items } = order;
  
  // Priority 1: Needs Assignment (most urgent)
  if (unassigned_items > 0) {
    return {
      status: 'Needs Assignment',
      severity: 'danger',
      icon: '⚠️',
      count: unassigned_items,
      tooltipText: `${unassigned_items} item${unassigned_items !== 1 ? 's' : ''} need${unassigned_items === 1 ? 's' : ''} to be assigned to a fulfillment location. Click to assign these items.`
    };
  }
  
  // Priority 2: Awaiting Inspection
  if ((pending_review_items || 0) > 0) {
    return {
      status: 'Awaiting Inspection',
      severity: 'warning',
      icon: '👁️',
      count: pending_review_items,
      tooltipText: `${pending_review_items} item${pending_review_items !== 1 ? 's' : ''} ${pending_review_items === 1 ? 'is' : 'are'} waiting for quality inspection. Click to review these items.`
    };
  }
  
  // Priority 3: Items Declined
  if ((declined_items || 0) > 0) {
    return {
      status: 'Items Declined',
      severity: 'danger',
      icon: '❌',
      count: declined_items,
      tooltipText: `${declined_items} item${declined_items !== 1 ? 's' : ''} ${declined_items === 1 ? 'has' : 'have'} been declined. Click to reassign these items.`
    };
  }
  
  // Priority 4: In Progress (all assigned but not completed)
  if (assigned_items === total_items && assigned_items > 0) {
    return {
      status: 'In Progress',
      severity: 'info',
      icon: '🔄',
      count: assigned_items,
      tooltipText: `All ${assigned_items} item${assigned_items !== 1 ? 's' : ''} ${assigned_items === 1 ? 'has' : 'have'} been assigned. Click to view order details.`
    };
  }
  
  // Priority 5: No Action Needed
  return {
    status: 'No Action Needed',
    severity: 'success',
    icon: '✅',
    count: 0,
    tooltipText: 'This order has been completed successfully. Click to view order details.'
  };
};

// Text color utility
const getTextColorClass = (severity: string): string => {
  switch (severity) {
    case 'danger': return 'text-danger';
    case 'warning': return 'text-warning';
    case 'info': return 'text-info';
    case 'success': return 'text-success';
    default: return 'text-muted';
  }
};

// Main template function
export const orderStatusTemplate = (rowData: ShippingOrder, onClick?: (order: ShippingOrder, statusType: string) => void) => {
  const priorityInfo = calculatePriorityStatus(rowData);
  const tooltipId = `priority-tooltip-${rowData.order_id}`;
  const isClickable = !!onClick;
  
  const handleClick = () => {
    if (onClick) {
      onClick(rowData, priorityInfo.status);
    }
  };
  
  return (
    <div>
      <span
        className={`fw-semibold ${getTextColorClass(priorityInfo.severity)} ${isClickable ? 'cursor-pointer' : ''}`}
        data-pr-tooltip={priorityInfo.tooltipText}
        data-pr-position="left"
        id={tooltipId}
        onClick={handleClick}
        style={isClickable ? { 
          cursor: 'pointer'
        } : undefined}
      >
        <span className="me-2">{priorityInfo.icon}</span>
        {priorityInfo.status}
      </span>
      <Tooltip target={`#${tooltipId}`} style={{ maxWidth: '300px', whiteSpace: 'normal' }} />
    </div>
  );
};